import React, { useCallback, useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { formValueSelector } from 'redux-form'
import Field from 'redux-form/lib/Field'
import { formatCurrency } from 'ggx-service/i18n/format-currency.service'
import { LoadingSpinner } from 'ggx-src/legacy/views/helpers/loading/spinner'
import { Card } from 'ggx-componentlibrary/components/card/card.component'
import {
  StyledHeading,
  StyledSubheading,
  StyledTotal,
  StyledError,
  StyledPaymentWrapper,
  LoadingContainer,
} from 'ggx-src/delivery/order/order-form/payment-summary-form/payment-summary.component.styles'
import BET_4052 from 'ggx-src/feat/BET_4052/index.js'
import { ConnectedPaymentMethodSelection } from 'ggx-src/payment/payment-method-selection.component'
import { getTotalCredit } from 'ggx-src/billing/duck/selectors'

import { getFeatureFlag } from 'ggx-service/feature-flag/selectors'
import { displayAlert } from 'ggx-componentlibrary/components/alerts/alerts.component'
import { PaymentMethodModal } from 'ggx-src/van-booking/order/sidebar/order-form/summary/payment-method/payment-method-modal/payment-method-modal.component'
import RightIconSVG from 'ggx-src/van-booking/order/sidebar/order-form/summary/payment-method/assets/right_icon.svg'
import { Body } from 'ggx-componentlibrary/design/typography/typography.styles'
import {
  getSavedCardInfo,
  getStripeCards,
} from 'ggx-src/payment/duck/selectors'
import { CreditCardDisplay as NewCreditCardDisplay } from 'ggx-src/van-booking/order/sidebar/order-form/summary/payment-method/credit-card-display.component'
import { Wallet } from 'ggx-src/payment/wallet/wallet.multiple.cards.component'
import getCountry from 'ggx-src/legacy/views/helpers/get_country'
import { CouponSection } from './coupon-section.component'
import { useIsDPMonthlySettlementEnabled } from '../use-monthly-state'
import { MonthlySettlementDisplay } from './monthly-settlement-display.component'

const PaymentSummaryForm = ({
  className,
  quotation,
  quotationLoading,
  quotationMeta,
  input,
  t,
  paymentType,
  totalCredit,
  savedCardInfo,
  stripeCards,
  featureFlagEnableCouponFeature,
}) => {
  const isDPMonthlySettlementEnabled = useIsDPMonthlySettlementEnabled()
  const quotationBreakdown = quotation.breakdown
  const hasQuotation = quotation.total !== 0
  const paymentMethods = ['credit_card', 'prepaid_wallet']
  const defaultPaymentMethod = isDPMonthlySettlementEnabled
    ? 'cash_credit'
    : totalCredit > 0
    ? 'prepaid_wallet'
    : paymentMethods[0]
  const [show, setShow] = useState(false)
  useEffect(() => {
    input.onChange(defaultPaymentMethod)
  }, [])
  const handlePaymentMethodChange = useCallback(
    paymentMethod => {
      input.onChange(paymentMethod)
    },
    [input]
  )
  const displayedPaymentMethod = useMemo(() => {
    const selectedPaymentMethod = paymentType || defaultPaymentMethod
    switch (selectedPaymentMethod) {
      case 'cash':
        return t('payment.paymentMethods.cash')
      case 'prepaid_wallet':
        return `${t('common__wallet')} (${formatCurrency({
          amount: totalCredit,
        })})`
      default:
        if (selectedPaymentMethod.startsWith('credit_card')) {
          const id =
            Number(selectedPaymentMethod.replace('credit_card_', '')) ||
            savedCardInfo.id
          const card = stripeCards.find(card => card.id === id)
          if (card)
            return (
              <NewCreditCardDisplay
                nickname={card.nickname}
                brand={card.brand}
                last4={card.last4}
                isExpired={card.is_expired}
                noBorderDisplayMode
              />
            )
        }
        return t('status__pay_with__not_selected')
    }
  }, [
    paymentType,
    defaultPaymentMethod,
    t,
    totalCredit,
    savedCardInfo,
    stripeCards,
  ])

  const voucher = quotation?.discount?.user_vouchers?.[0]
  const discount = voucher?.discount
  useEffect(() => {
    if (featureFlagEnableCouponFeature) {
      if (discount) {
        displayAlert({
          message: t('feedback__coupon_applied', {
            discount: formatCurrency({ amount: Math.abs(discount) }),
          }),
          autoClose: true,
        })
      }
    }
  }, [featureFlagEnableCouponFeature, discount, displayAlert, t])

  const country = getCountry()
  useEffect(() => {
    if (country === 'HK' || country === 'SG') {
      if (paymentType !== input.value) {
        const paymentMethods = [
          `credit_card_${savedCardInfo?.id}`,
          'prepaid_wallet',
        ]
        const defaultPaymentMethod = isDPMonthlySettlementEnabled
          ? 'cash_credit'
          : totalCredit > 0 || !savedCardInfo?.id
          ? 'prepaid_wallet'
          : paymentMethods[0]
        const paymentNewValue = paymentType || defaultPaymentMethod
        handlePaymentMethodChange(paymentNewValue)
        if (
          paymentNewValue.startsWith('credit_card') &&
          stripeCards?.find(card => card.is_default)?.is_expired
        ) {
          displayAlert({
            message: t('error__card_expired_place_order'),
            type: 'error',
          })
        }
      }
    }
  }, [
    country,
    paymentType,
    isDPMonthlySettlementEnabled,
    input,
    savedCardInfo,
    totalCredit,
    stripeCards,
    handlePaymentMethodChange,
    displayAlert,
  ])

  const orderPrice = Number(quotation.total) || 0 + Number(discount) || 0

  return (
    <div>
      <Card className={className}>
        {quotationLoading ? (
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        ) : (
          <div>
            <StyledHeading typographyStyles="mb-xs">
              {t('orderDetails.shippingInfo.priceBreakdown.total')}
            </StyledHeading>
            <StyledTotal total={quotation.total}>
              {formatCurrency({ amount: quotation.total })}
            </StyledTotal>
            {hasQuotation && (
              <>
                <div>
                  <StyledSubheading
                    style={{ marginTop: '20px', marginBottom: '8px' }}
                  >
                    {t('orderDetails.title.shippingInfo.priceBreakdown.name')}
                  </StyledSubheading>
                  <BET_4052.OrderPriceBreakdown
                    additionalCharges={quotationBreakdown}
                    total={quotation.total}
                    discount={quotation.discount}
                  />
                </div>
                <CouponSection orderPrice={orderPrice} code={voucher?.code} />
              </>
            )}
          </div>
        )}
        {quotationMeta.error && (
          <StyledError>{quotationMeta.error}</StyledError>
        )}
        <StyledPaymentWrapper>
          {isDPMonthlySettlementEnabled ? (
            <MonthlySettlementDisplay />
          ) : getCountry() === 'HK' || getCountry() === 'SG' ? (
            <>
              <StyledHeading onClick={() => setShow(true)}>
                {t('orders.new.basicInfo.subtitle.payWith')}
                <RightIconSVG />
              </StyledHeading>
              {show && (
                <PaymentMethodModal
                  handleModalClose={() => setShow(false)}
                  selectedPaymentMethod={paymentType || defaultPaymentMethod}
                  setSelectedPaymentMethod={handlePaymentMethodChange}
                  sufficientBalance={totalCredit > Number(quotation.total)}
                  availablePaymentMethods={paymentMethods}
                />
              )}
              <Body typographyStyles="mb-none c-grey_dark">
                {displayedPaymentMethod}
              </Body>
              <Field
                component={Wallet}
                name="hasSufficientBalance"
                openModal={false}
                orderQuotation={parseInt(quotation.total, 10)}
                prepaidBalance={totalCredit}
                hideDisclaimers={false}
                showMessage={
                  (paymentType || defaultPaymentMethod) === 'prepaid_wallet'
                }
              />
            </>
          ) : (
            <>
              <StyledHeading>
                {t('orders.new.basicInfo.subtitle.payWith')}
              </StyledHeading>
              <ConnectedPaymentMethodSelection
                paymentType={paymentType || defaultPaymentMethod}
                options={paymentMethods}
                onPaymentMethodChange={handlePaymentMethodChange}
                orderQuotation={parseInt(quotation.total, 10)}
              />
            </>
          )}
        </StyledPaymentWrapper>
      </Card>
    </div>
  )
}
PaymentSummaryForm.propTypes = {
  className: PropTypes.string,
  quotation: PropTypes.object,
  quotationLoading: PropTypes.bool,
  quotationMeta: PropTypes.shape({
    error: PropTypes.string,
  }),
  input: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func,
  paymentType: PropTypes.string,
  totalCredit: PropTypes.number,
  savedCardInfo: PropTypes.object,
  stripeCards: PropTypes.array,
  featureFlagEnableCouponFeature: PropTypes.bool,
}
PaymentSummaryForm.defaultProps = {
  quotation: {
    breakdown: [],
    total: 0,
  },
  quotationLoading: false,
  quotationMeta: {},
  featureFlagEnableCouponFeature: false,
}

const selector = formValueSelector('delivery-order')

const mapStateToProps = state => ({
  totalCredit: getTotalCredit(state),
  paymentType: selector(state, 'payment_type'),
  featureFlagEnableCouponFeature: getFeatureFlag({
    state,
    featureFlag: 'enable_coupon_feature',
  }),
  savedCardInfo: getSavedCardInfo(state),
  stripeCards: getStripeCards(state) ?? [],
})

const PaymentSummaryFormWithTranslation = withTranslation()(PaymentSummaryForm)
const ConnectedPaymentSummaryFormWithTranslation = connect(mapStateToProps)(
  PaymentSummaryFormWithTranslation
)

export {
  PaymentSummaryFormWithTranslation,
  ConnectedPaymentSummaryFormWithTranslation,
}
